// MUI アイコン（CloseIcon）をインポート
import CloseIcon from '@mui/icons-material/Close';
// MUI から Box、Button、Grid、IconButton、Modal、Stack、SxProps、Typography コンポーネントをインポート
import { Box, Button, Grid, IconButton, Modal, Stack, SxProps, Typography } from '@mui/material';
// React とフックをインポート
import { useEffect } from 'react';
// react-hook-form から useForm フックをインポート
import { useForm } from 'react-hook-form';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
import RHFDeskTopDatePicker from 'src/components/react-hook-form/RHFDeskTopDatePicker';
import { REMINDER_STATUS } from 'src/constants';
import useSettings from 'src/hooks/useSettings';
// Redux ストアセレクターをインポート
import { RootState, useAppDispatch, useAppSelector } from 'src/redux/store';
import palette from 'src/theme/palette';
// リマインドデータエンティティをインポート
import { ReminderEntity } from '../hooks/useReminderListGrid';
// バリデーションルールをインポート
import { useValidSaveButton } from '../hooks/useValidSaveButton';
// リマインド設定アクションをインポート
import { setReminderListData } from '../store/enqueteCalendarSlice';
// プロパティインターフェースを定義
type UpdateSentAtModalProps = {
  // リマインド日付
  reminderDate: string;
  // リマインドID
  reminderId: string;
  // ダイアログの開閉状態保持 (true：開く、false：閉じる)
  open: boolean;
  // ダイアログクローズ時動作。openをfalseに変更
  onClose: () => void;
};
// リマインド日付変更ダイアログ
export const UpdateSentAtModal = ({ reminderDate, reminderId, open, onClose }: UpdateSentAtModalProps) => {
  // 翻訳関数を取得
  const { t } = useTranslation();
  const methods = useForm({
    mode: 'onChange',
  });
  // Validator
  const { reminderDateRule } = useValidSaveButton(methods);
  // Redux dispatch 関数を取得
  const dispatch = useAppDispatch();
  const { themeMode } = useSettings();
  const isLight = themeMode === 'light';
  // Redux ストアからリマインドデータを取得
  const reminderListData = useAppSelector((state: RootState) => state.enqueteCalendar.reminderListData);

  // 初期
  useEffect(() => {
    if (open) {
      methods.setValue('reminderDate', reminderDate);
    }
  }, [open]);
  // 設定押下
  const dateSave = async () => {
    const valid = await methods.trigger();

    if (!valid) {
      // エラーある
      return;
    }

    // リマインド日付
    const sentAt = methods.getValues('sentAt');
    // リマインドデータ編集
    // リマインド日付 ＝ 画面.リマインド日付
    // リマインドステータス ＝ 送信待ち
    // 更新フラグ ＝ 更新
    const newListData: ReminderEntity[] = reminderListData.map((item) =>
      (item.id === undefined && 'addRowNum_' + item.addRowNum === reminderId) || (item.id !== undefined && item.id === reminderId)
        ? {
            ...item,
            sentAt: sentAt ? new Date(sentAt - new Date().getTimezoneOffset() * 60 * 1000).toISOString() : item.sentAt,
            status: REMINDER_STATUS.ACTIVE,
            updateFlag: true,
          }
        : item,
    );
    // リマインドデータ更新
    dispatch(setReminderListData(newListData));
    // 画面を閉じる
    onClose();
  };

  //-------------------------------------------------------------
  // SxProps
  //-------------------------------------------------------------
  const modalStyle: SxProps = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
    width: '480px!important',
    bgcolor: isLight ? palette.light.background.paper : palette.dark.background.paper,
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };

  // キャンセル処理関数
  const cancel = () => {
    methods.clearErrors('reminderDate');
    onClose();
  };

  return (
    <Modal open={open} onClose={cancel}>
      <Box sx={modalStyle}>
        {/* 閉じるボタン（IconButton） */}
        <IconButton
          // aria-label 属性
          aria-label="close"
          // クリックイベント（キャンセル確認）
          onClick={cancel}
          // スタイル設定（位置、色）
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}>
          {/* 閉じるアイコン */}
          <CloseIcon />
        </IconButton>
        <Stack spacing={2}>
          {/* modal title */}
          <Grid container>
            <Grid item xs={12}>
              <Typography variant={'h4'} component={'h2'} children={t('CalendarCreate.reminderUpdate.pageTitle')} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid container item direction="row" justifyContent="flex-start" style={{ paddingLeft: 0 }}>
              <Grid item xs={4}>
                <Typography variant="subtitle1" sx={{ wordBreak: 'keep-all', textAlign: 'left', mt: 1 }}>
                  {t('CalendarCreate.reminderUpdate.reminderDate')}
                </Typography>
              </Grid>
              <Grid item xs={7} sx={{ width: '260px' }}>
                <RHFDeskTopDatePicker
                  // フォーム制御オブジェクト
                  control={methods.control}
                  // 入力マスク
                  mask="____/__/__"
                  // フィールド名
                  name="reminderDate"
                  // 表示ラベル
                  label={t('CalendarCreate.reminderUpdate.reminderDate')}
                  // バリデーションルール
                  rules={reminderDateRule}
                  // 日付選択の表示単位:年月日
                  views={['year', 'month', 'day']}
                  // リマインド日付フォマード
                  inputFormat={'yyyy/MM/dd'}
                  // 幅を100%にする
                  fullWidth
                  // 変更イベント
                  onChange={(value) => {
                    methods.setValue('sentAt', value);
                  }}
                  // スタイル設定
                  sx={{ width: '245px' }}
                />
              </Grid>
            </Grid>
            <Grid item container justifyContent="flex-end" alignItems="center" xs={12} md={11}>
              <Grid item xs={12} md={2.5} sx={{ mr: 2, width: '100%', textAlign: 'right' }}>
                {/* 更新ボタン */}
                <Button
                  // 塗りつぶし
                  variant={'contained'}
                  // クリックイベント（更新）
                  onClick={methods.handleSubmit(dateSave)}
                  // 表示内容
                  children={t('button.update')}
                  // スタイル設定
                  sx={{ ml: 2 }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </Modal>
  );
};
